/**
 * Inbox API client (SPEC E1c — unified-inbox, R4).
 *
 * Talks to the R3 inbox endpoints + the E0 contact/opportunity endpoints + the
 * R1 channel-send endpoint. Same Firebase-ID-token auth + fetch pattern as
 * lib/api/aiDrafts.ts / lib/api/pipeline.ts.
 */

import { auth } from '../firebase';
import type {
  InboxListResponse,
  ContactConversationsResponse,
  ConversationDetail,
  ContactDetail,
  ContactOpportunity,
  PendingDraft,
  InboxMessage,
} from '@/types/inbox';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handle<T>(response: Response, what: string): Promise<T> {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    const detail =
      typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
    throw new Error(detail || `Failed to ${what}: ${response.statusText}`);
  }
  return response.json();
}

/** GET /api/inbox — the contact-threaded, conversation-gated inbox list. */
export async function listInbox(params: {
  channel?: string;
  status?: string;
  assignee_id?: string;
  limit?: number;
  after_contact_id?: string;
} = {}): Promise<InboxListResponse> {
  const headers = await getAuthHeader();
  const qs = new URLSearchParams();
  if (params.channel) qs.append('channel', params.channel);
  if (params.status) qs.append('status', params.status);
  if (params.assignee_id) qs.append('assignee_id', params.assignee_id);
  if (params.limit !== undefined) qs.append('limit', String(params.limit));
  if (params.after_contact_id) qs.append('after_contact_id', params.after_contact_id);
  const url = `${API_BASE_URL}/api/inbox${qs.toString() ? `?${qs}` : ''}`;
  const response = await fetch(url, { headers });
  return handle<InboxListResponse>(response, 'load inbox');
}

/** GET /api/contacts/{id}/conversations — the contact's threads (enriched). */
export async function listContactConversations(
  contactId: string
): Promise<ContactConversationsResponse> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/contacts/${encodeURIComponent(contactId)}/conversations`,
    { headers }
  );
  return handle<ContactConversationsResponse>(response, 'load contact conversations');
}

/** GET /api/conversations/{id} — the full thread (recent messages). */
export async function getConversation(
  conversationId: string
): Promise<ConversationDetail> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(conversationId)}`,
    { headers }
  );
  return handle<ConversationDetail>(response, 'load conversation');
}

/** POST /api/conversations/{id}/send — DIRECT operator send via the channel adapter. */
export async function sendMessage(
  conversationId: string,
  content: string
): Promise<{ message_id: string; conversation_id: string; channel: string }> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(conversationId)}/send`,
    { method: 'POST', headers, body: JSON.stringify({ content }) }
  );
  return handle(response, 'send message');
}

/** POST /api/conversations/{id}/note — an internal note (NOT channel-sent). */
export async function addInternalNote(
  conversationId: string,
  content: string
): Promise<{ message_id: string; conversation_id: string; direction: string }> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/conversations/${encodeURIComponent(conversationId)}/note`,
    { method: 'POST', headers, body: JSON.stringify({ content }) }
  );
  return handle(response, 'add note');
}

/** GET /api/contacts/{id} — the contact-context panel data. */
export async function getContact(contactId: string): Promise<ContactDetail> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/contacts/${encodeURIComponent(contactId)}`,
    { headers }
  );
  return handle<ContactDetail>(response, 'load contact');
}

/** GET /api/contacts/{id}/opportunities — the contact's open opportunities. */
export async function getContactOpportunities(
  contactId: string
): Promise<ContactOpportunity[]> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/contacts/${encodeURIComponent(contactId)}/opportunities`,
    { headers }
  );
  const data = await handle<{ opportunities: ContactOpportunity[] }>(
    response,
    'load opportunities'
  );
  return data.opportunities ?? [];
}

/** GET /api/ai-drafts?status=pending&conversation_id= — the pending AI draft for
 * inline draft-assist ("Suggest a reply" / "Use draft"). */
export async function getPendingDraft(
  conversationId: string
): Promise<PendingDraft | null> {
  const headers = await getAuthHeader();
  const qs = new URLSearchParams({
    status: 'pending',
    conversation_id: conversationId,
  });
  const response = await fetch(`${API_BASE_URL}/api/ai-drafts?${qs}`, { headers });
  const data = await handle<{ drafts: PendingDraft[] }>(response, 'load pending draft');
  return data.drafts && data.drafts.length > 0 ? data.drafts[0] : null;
}

/** Re-export the message type so consumers can import it from one place. */
export type { InboxMessage };
